if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        const quickCheckoutButton = evt.submitter?.matches('[data-careiz-quick-checkout]') ? evt.submitter : null;
        if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (quickCheckoutButton?.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        if (quickCheckoutButton) quickCheckoutButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const careizUpsells = document.querySelector(`careiz-upsells[data-product-form="${this.form.getAttribute('id')}"]`);
        if (careizUpsells) await careizUpsells.cartQueue;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        const careizItems = careizUpsells?.selectedItems() || [];
        {
          const properties = {};
          for (const [name, value] of formData.entries()) {
            const match = name.match(/^properties\[([^\]]+)\]$/);
            if (match && typeof value === 'string') properties[match[1]] = value;
          }
          const mainItem = {
            id: Number(formData.get('id')),
            quantity: Math.max(1, Number(formData.get('quantity') || 1)),
            properties,
          };
          if (formData.get('selling_plan')) mainItem.selling_plan = Number(formData.get('selling_plan'));
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify({
            items: [mainItem, ...careizItems],
            ...(this.cart ? {
              sections: this.cart.getSectionsToRender().map((section) => section.id),
              sections_url: window.location.pathname,
            } : {}),
          });
        }

        let cartBefore = null;
        let addSucceeded = false;
        try {
          cartBefore = await this.cartSnapshot();
        } catch (_) {
          // A failed read must not prevent the customer from adding an item.
        }

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            addSucceeded = !response.status;
            if (!response.status && response.items) {
              response.id = response.items[0]?.id;
              careizUpsells?.itemsAdded(response);
              if (careizUpsells) response = await careizUpsells.finalizeCart(response);
              if (careizUpsells?.cartWarning) this.handleErrorMessage(careizUpsells.cartWarning);
            }
            if (!response.status && window.CareizCart && !careizUpsells) {
              const normalized = await window.CareizCart.normalize();
              response = {...normalized.cart,id:response.id,sections:normalized.cart.sections || response.sections};
            }
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = quickCheckoutButton ? this.dataset.careizCheckoutUrl : window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: response });
            this.error = false;
            if (quickCheckoutButton) {
              window.location.assign(this.dataset.careizCheckoutUrl);
              return;
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch(async (e) => {
            console.error(e);
            let cartAfter = null;
            try {
              cartAfter = await this.cartSnapshot();
            } catch (_) {
              // Leave the result uncertain if the cart cannot be read either.
            }
            const variantId = Number(formData.get('id'));
            const quantity = Math.max(1, Number(formData.get('quantity') || 1));
            const count = (cart) => cart.items.reduce(
              (sum, item) => sum + (Number(item.variant_id || item.id) === variantId ? item.quantity : 0), 0
            );
            const additionConfirmed = cartBefore && cartAfter && count(cartAfter) >= count(cartBefore) + quantity;
            if (addSucceeded || additionConfirmed) {
              window.location.assign(window.routes.cart_url);
              return;
            }
            this.handleErrorMessage(window.cartStrings?.addUncertain || window.cartStrings?.error);
            this.showCartRecovery();
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (addSucceeded && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              if (quickCheckoutButton) quickCheckoutButton.removeAttribute('aria-disabled');
            }
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      async cartSnapshot() {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 4000);
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
            signal: controller.signal,
          });
          if (!response.ok) throw new Error('Unable to read cart');
          const cart = await response.json();
          if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
          return cart;
        } finally {
          clearTimeout(timeout);
        }
      }

      showCartRecovery() {
        if (this.hideErrors || !this.errorMessageWrapper) return;
        const link = document.createElement('a');
        link.className = 'product-form__cart-recovery underlined-link';
        link.href = window.routes.cart_url;
        link.textContent = window.cartStrings.checkCart;
        this.errorMessage.append(document.createTextNode(' '), link);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}


